"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { useSession } from "next-auth/react";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n/context";

function variantFor(tier: "pro" | "premium", cycle: "monthly" | "yearly"): string | undefined {
  if (tier === "pro" && cycle === "monthly") return process.env.NEXT_PUBLIC_LS_VARIANT_PRO_MONTHLY;
  if (tier === "pro" && cycle === "yearly") return process.env.NEXT_PUBLIC_LS_VARIANT_PRO_YEARLY;
  if (tier === "premium" && cycle === "monthly") return process.env.NEXT_PUBLIC_LS_VARIANT_PREMIUM_MONTHLY;
  if (tier === "premium" && cycle === "yearly") return process.env.NEXT_PUBLIC_LS_VARIANT_PREMIUM_YEARLY;
  return undefined;
}

export function PricingSection({ asPage = false }: { asPage?: boolean } = {}) {
  const { t } = useTranslation();
  // On the standalone /pricing route this section IS the page, so its lead
  // line must be the document's single <h1>. Inlined into the landing page it
  // stays an <h2> (the hero owns the <h1> there).
  const Heading = asPage ? "h1" : "h2";
  const p = t.landing.cover.pricing;
  const router = useRouter();
  const { data: session } = useSession();
  const [cycle, setCycle] = useState<"yearly" | "monthly">("yearly");
  const [busy, setBusy] = useState<string | null>(null);

  async function handleCta(tier: "pro" | "premium") {
    const variant = variantFor(tier, cycle);
    if (!variant) {
      toast.error("Plan not configured yet");
      return;
    }
    if (!session) {
      router.push(`/register?next=${encodeURIComponent(`/checkout?variant=${variant}`)}`);
      return;
    }
    setBusy(tier);
    try {
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variantId: variant }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.error || "Checkout failed");
      }
    } catch {
      toast.error("Checkout failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section id="pricing" className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-24 md:py-[120px] z-[2]">
      <div className="text-center max-w-[720px] mx-auto mb-8">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {p.kicker}
        </div>
        <Heading
          className="font-display text-[clamp(38px,4.2vw,58px)] leading-[1.04] tracking-[-0.02em] text-balance"
          style={{ fontWeight: 380 }}
        >
          {p.headlineA}
          <br />
          {p.headlineB}
        </Heading>
      </div>

      <div className="text-center mb-14">
        <div className="inline-flex bg-[var(--paper)] border border-[var(--rule)] rounded-full p-1 font-mono text-[12px] tracking-[0.14em] uppercase">
          {(["monthly", "yearly"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setCycle(k)}
              className={`px-[22px] py-2.5 rounded-full transition ${
                cycle === k ? "bg-[var(--terracotta)] text-[var(--terracotta-soft)]" : "text-[var(--ink-soft)]"
              }`}
            >
              {k === "monthly" ? p.toggleMonthly : p.toggleYearly}
              {k === "yearly" && (
                <span className="ml-2 opacity-85 text-[10px] normal-case tracking-wider">
                  {p.toggleSave}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 max-w-[820px] mx-auto items-stretch">
        {p.plans.map((plan, i) => {
          const isPro = i === 1;
          const price = cycle === "yearly" ? plan.priceYearly : plan.priceMonthly;
          const sub = cycle === "yearly" ? plan.subYearly : plan.subMonthly;
          // No free tier any more — every plan is a paid plan with /month pricing.
          const isFree = false;
          void i;
          return (
            <div
              key={plan.eyebrow}
              className={`relative flex flex-col rounded-[24px] p-9 bg-[var(--paper)] ${
                isPro
                  ? "border-2 border-[var(--terracotta)] shadow-[0_30px_80px_rgba(184,83,26,0.12)]"
                  : "border border-[var(--rule)]"
              }`}
            >
              {isPro && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[var(--terracotta)] text-[var(--terracotta-soft)] px-3.5 py-1.5 rounded-full font-mono text-[11px] tracking-[0.16em] uppercase">
                  {p.badgePopular}
                </div>
              )}
              <div
                className={`font-mono text-[11px] tracking-[0.18em] uppercase mb-2.5 ${
                  isPro ? "text-[var(--terracotta)]" : "text-[var(--ink-soft)]"
                }`}
              >
                {plan.eyebrow}
              </div>
              <h3 className="font-display text-[30px] leading-[1.1] tracking-[-0.015em] mb-5" style={{ fontWeight: 400 }}>
                {plan.title}
              </h3>
              <div className="flex items-baseline gap-1.5 mb-0.5">
                <span className="font-display text-[48px] leading-none tracking-[-0.025em]" style={{ fontWeight: 400 }}>
                  {price}
                </span>
                <span className="text-sm text-[var(--ink-soft)]">
                  {isFree ? p.forever : p.perMonth}
                </span>
              </div>
              <div className="font-mono text-[11px] text-[var(--ink-soft)] tracking-[0.08em] mb-6 min-h-[14px]">
                {sub || "\u00A0"}
              </div>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => handleCta(isPro ? "pro" : "premium")}
                className={`w-full inline-flex items-center justify-center gap-2 px-[18px] py-[14px] rounded-[10px] font-medium text-[14px] transition hover:-translate-y-px disabled:opacity-60 ${
                  isPro
                    ? "bg-[var(--terracotta)] text-[var(--terracotta-soft)] hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
                    : "border border-[var(--rule)] text-[var(--ink)] hover:bg-[rgba(26,24,21,0.04)]"
                }`}
              >
                {plan.cta}
              </button>
              <div className="font-mono text-[10px] tracking-[0.1em] text-[var(--ink-soft)] text-center mt-2.5 min-h-[14px]">
                {p.vatIncluded}{!isFree && <> · {p.noCard}</>}
              </div>
              <ul className="list-none pt-7 mt-7 border-t border-[var(--hair)] text-sm leading-[1.55]">
                {plan.features.map((f) => {
                  // `[soon]` (or `[kmalu]`) suffix → tiny mono badge after
                  // the label. Lets translation strings stay plain text.
                  const soonMatch = f.match(/^(.*?)\s*\[(soon|kmalu)\]\s*$/i);
                  const label = soonMatch ? soonMatch[1] : f;
                  const showSoon = !!soonMatch;
                  return (
                    <li key={f} className="flex gap-2.5 py-2 items-start">
                      <svg
                        className="flex-shrink-0 mt-1"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="var(--terracotta)"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 7l3 3 5-6" />
                      </svg>
                      <span>
                        {showSoon ? (() => {
                          // Wrap LAST word + badge in `whitespace-nowrap` so
                          // they always stay on the same line. If the column
                          // is too narrow, the whole unit wraps together —
                          // the chip never sits alone on a new line.
                          const m = label.match(/^(.*?\s)?(\S+)$/);
                          const head = m?.[1] ?? "";
                          const tail = m?.[2] ?? label;
                          return (
                            <>
                              {head}
                              <span className="whitespace-nowrap">
                                {tail}
                                {" "}
                                <span className="inline-block rounded-full border border-[var(--rule)] bg-[var(--paper-2)] px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
                                  soon
                                </span>
                              </span>
                            </>
                          );
                        })() : label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="max-w-[680px] mx-auto mt-20 text-center">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {p.promise.kicker}
        </div>
        <p
          className="font-display italic text-[22px] leading-[1.55] text-[var(--ink)] text-balance my-3"
          style={{ fontWeight: 400 }}
        >
          {p.promise.text}{" "}
          <span className="text-[var(--terracotta)]">{p.promise.coda}</span>
        </p>
        <div className="font-mono text-[12px] tracking-[0.14em] text-[var(--ink-soft)]">
          {p.promise.sign}
        </div>
      </div>
    </section>
  );
}
